/**
 * SpikeHandoff — V105-7-2: "Hand this to whoever runs CI" under an issued feasibility check.
 * Round-1 critic B1; round-2 R2-H1..H3 and the owner decision 2026-10-09 (self-service CI trust).
 *
 * Two tabs, one per runner that can run a feasibility check (ADR-0074 §3); one line under the tabs
 * says in-network collectors run standing checks only:
 *   - GitHub Actions: "Do this first" — the workspace's trusted CI workflow (Settings → Security → CI
 *     identities; owners / admins see it and get "Trust a workflow", others ask an admin). Then a
 *     copyable workflow that installs the runner ONLY from the release archive pinned by sha256
 *     (V105-7-7 H-1, never `npx` / a package name), with `permissions: id-token: write`,
 *     `--identity --sign-result` and `--environment sandbox`. Owners / admins create the CI token
 *     right here (exactly verification:read + verification:write, shown once, audited server side).
 *   - Manual runner: register once with a one-time code (owners / admins create it here, purpose
 *     `verification_runner`, shown once, audited), download the check, run the image by digest.
 * Every runner value (archive URL + sha256, image digest, signer identity) comes from ONE source,
 * release/gacpro-trust.json; while the release isn't published the tabs say so and offer support —
 * copied text never carries a placeholder, and the check is not marked handed off.
 * A one-time secret takes focus and is announced once (R2-H3) — only right after it is created,
 * never again on a remount (round-3 A-H1); its value lives in the parent row state, so it survives
 * the panel collapsing or a result arriving. While GAC Pro hasn't published the runner or the
 * signer, only the blocks that need them say so (round-3 F-H2); token, trust and download stay.
 */
import { useCallback, useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button } from '@aigovops/design-system';
import { createApiToken, createCustomerCollector } from '../../lib/api';
import { bundleDownloadUrl } from '../../lib/infra-plan-api';
import { listCiIdentities, trustWorkflowHref } from '../../lib/ci-identities-api';
import { runnerGap, runnerRelease, signerPin, type RunnerRelease, type SignerPin } from '../../lib/runner-release';
import { AskAdmin } from '../AskAdmin';
import { CommandBlock, runnerRegisterCommand, runnerVerifyCommand } from '../CustomerCollectors';

type Tab = 'github' | 'manual';
const TABS: Array<{ key: Tab; label: string }> = [{ key: 'github', label: 'GitHub Actions' }, { key: 'manual', label: 'Manual runner' }];
export const WORKFLOW_FILE = '.github/workflows/gacpro-feasibility.yml';
const SUPPORT = (what: string) => `mailto:support@gacpro.io?subject=${encodeURIComponent(what)}`;

/** The one-time secrets a row made (kept by the row so they survive collapse / refetch). */
export interface HandoffSecrets { token?: string; code?: string }

/** `issuer#identity` → the pair the runner pins (kept for callers that only need the parse). */
export function signerOf(signerIdentity: string | null | undefined): { issuer: string; identity: string } | null {
  const p = signerPin(signerIdentity);
  return p ? { issuer: p.issuer, identity: p.identity } : null;
}

const GH = (expr: string) => '$' + `{{ ${expr} }}`;

/** The one-off GitHub workflow for one spike bundle: the pinned install form, every value literal. */
export function githubWorkflow(o: { bundleId: string; signer: SignerPin; baseUrl: string; release: RunnerRelease; workflowFile?: string }): string {
  return [
    `# ${o.workflowFile ?? WORKFLOW_FILE} — run it from the Actions tab (Run workflow)`,
    'name: GAC Pro feasibility check',
    'on: workflow_dispatch',
    'permissions:',
    '  contents: read',
    '  id-token: write   # GAC Pro confirms who ran it, and the result is signed keyless',
    'jobs:',
    '  feasibility:',
    '    runs-on: ubuntu-latest',
    '    timeout-minutes: 60',
    '    steps:',
    '      - uses: actions/checkout@v4',
    '      - uses: actions/setup-node@v4',
    '        with:',
    "          node-version: '22'",
    `      - name: Install the pinned GAC Pro runner${o.release.version ? ` ${o.release.version}` : ''}`,
    '        env:',
    `          GACPRO_RUNNER_URL: ${o.release.archiveUrl}`,
    `          GACPRO_RUNNER_SHA256: ${o.release.archiveSha256}`,
    '        run: |',
    '          set -euo pipefail',
    "          curl --fail --silent --show-error --location --proto '=https' --tlsv1.2 --output \"$RUNNER_TEMP/gacpro-runner.tgz\" \"$GACPRO_RUNNER_URL\"",
    '          echo "$GACPRO_RUNNER_SHA256  $RUNNER_TEMP/gacpro-runner.tgz" | sha256sum --check --strict -',
    '          mkdir -p "$RUNNER_TEMP/gacpro-runner"',
    '          tar -xzf "$RUNNER_TEMP/gacpro-runner.tgz" -C "$RUNNER_TEMP/gacpro-runner" --no-same-owner',
    '      - name: Run the feasibility check in the sandbox',
    '        env:',
    `          GACPRO_TOKEN: ${GH('secrets.GACPRO_TOKEN')}`,
    `          GACPRO_BASE_URL: ${o.baseUrl}`,
    `          GACPRO_SIGNER_IDENTITY: ${o.signer.identity}`,
    `          GACPRO_SIGNER_ISSUER: ${o.signer.issuer}`,
    '        # add --hooks .gacpro/hooks.json when the check runs your own command',
    '        run: >',
    '          node "$RUNNER_TEMP/gacpro-runner/cli.mjs" verify',
    `          --bundle ${o.bundleId}`,
    '          --environment sandbox',
    '          --identity --sign-result',
  ].join('\n');
}

function useCopied() {
  const [msg, setMsg] = useState('');
  const copy = async (text: string, what: string) => {
    setMsg('');
    try { await navigator.clipboard.writeText(text); window.setTimeout(() => setMsg(`${what} copied.`), 50); }
    catch { window.setTimeout(() => setMsg('Copy is blocked here. Select the text and press Ctrl+C (⌘C on Mac).'), 50); }
  };
  return { msg, copy };
}

/** A secret shown once: takes focus when it appears and is announced once (R2-H3). */
function ShownOnce({ label, value, note, focusNow, onFocused }: { label: string; value: string; note: string; focusNow: boolean; onFocused: () => void }) {
  const { msg, copy } = useCopied();
  const ref = useRef<HTMLDivElement>(null);
  // Round-3 A-H1: focus only right after it was created (a tab switch or reopening never steals focus).
  useEffect(() => { if (focusNow) { ref.current?.focus(); onFocused(); } }, [focusNow, onFocused]);
  return (
    <div ref={ref} tabIndex={-1} className="gp-pv-secret" role="group" aria-label={`${label} · shown only now`}>
      <code className="gp-pv-secret-v">{value}</code>
      <span className="gp-actions">
        <Button compact variant="secondary" onClick={() => void copy(value, label)}>Copy<span className="sr-only">{` ${label}`}</span></Button>
        <span className="gp-dim">{note}</span>
        <span role="status" className="gp-dim">{msg}</span>
      </span>
    </div>
  );
}

/** Round 3 (F-H2): names what GAC Pro hasn't published yet; replaces only the blocks that need it. */
export function NotPublished({ gap }: { gap: 'runner' | 'signer' }) {
  return gap === 'signer'
    ? <p className="gp-dim">Bundle signing isn't set up yet · <a href={SUPPORT('GAC Pro bundle signing')}>Ask GAC Pro support</a></p>
    : <p className="gp-dim">GAC Pro hasn't published the runner yet · <a href={SUPPORT('GAC Pro runner release')}>Ask GAC Pro support</a></p>;
}

/** The registration command shown once; focused only right after it was created (round-3 A-H1). */
function CodeShownOnce({ command, focusNow, onFocused }: { command: string; focusNow: boolean; onFocused: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => { if (focusNow) { ref.current?.focus(); onFocused(); } }, [focusNow, onFocused]);
  return (
    <div ref={ref} tabIndex={-1} role="group" aria-label="Registration code · shown only now">
      <CommandBlock label="Runner registration command · shown only now" text={command} copyLabel="Copy command" note="Works once · shown only now" />
    </div>
  );
}

export function SpikeHandoff({ slug, planId, label, title, bundleId, signerIdentity, canManage, headingId, secrets, onSecret, onHandedOff, returnTo }: {
  slug: string; planId: string; label: string; title: string; bundleId: string; signerIdentity: string | null;
  /** The in-app path "Trust a workflow" brings the person back to (validated by Settings). */
  returnTo?: string;
  /** Owner / admin: may create the CI token and the runner registration code, and trust a workflow (the server enforces). */
  canManage: boolean;
  headingId: string;
  secrets: HandoffSecrets;
  onSecret: (kind: keyof HandoffSecrets, value: string) => void;
  /** The person copied, emailed or downloaded the check (the plan view then waits for the result). */
  onHandedOff: () => void;
}) {
  const uid = useId();
  const [tab, setTab] = useState<Tab>('github');
  const [busy, setBusy] = useState<'token' | 'code' | null>(null);
  const [error, setError] = useState('');
  const [announce, setAnnounce] = useState('');
  const [justCreated, setJustCreated] = useState<keyof HandoffSecrets | null>(null);
  const clearJust = useCallback(() => setJustCreated(null), []);
  const tabs = useRef<Record<Tab, HTMLButtonElement | null>>({ github: null, manual: null });
  const { msg, copy } = useCopied();
  const release = runnerRelease();
  const signer = signerPin(signerIdentity);
  const ready = !!release && !!signer;
  const gap: 'runner' | 'signer' = runnerGap() ?? (!release ? 'runner' : 'signer');
  const origin = window.location.origin;
  const file = `${bundleId}.json`;
  const download = bundleDownloadUrl(slug, planId, bundleId);
  // Owner decision 2026-10-09: the workspace's own CI trust, shown first on the GitHub tab (owner / admin read).
  const trust = useQuery({ queryKey: ['ci-identities', slug], queryFn: () => listCiIdentities(slug), enabled: canManage, retry: false, staleTime: 30_000 });
  const trusted = (trust.data?.identities ?? []).filter((i) => i.status === 'active');
  const firstTrusted = trusted[0] ?? null;
  const branch = firstTrusted?.ref.replace(/^refs\/heads\//, '') ?? 'main';
  // Round-3 Medium: the workflow file follows the trusted one when there is one.
  const workflowFile = firstTrusted?.workflow_path ?? WORKFLOW_FILE;

  const workflow = ready ? githubWorkflow({ bundleId, signer: signer!, baseUrl: origin, release: release!, workflowFile }) : '';
  const verifyCmd = ready && release!.imageRef ? runnerVerifyCommand({ bundleFile: file, environment: 'sandbox', signerIdentity: signer!.identity, signerIssuer: signer!.issuer, imageRef: release!.imageRef }) : '';

  const instructions = (t: Tab): string => {
    const head = [`GAC Pro feasibility check: ${title} (${label})`, `Workspace: ${slug}`];
    return t === 'github'
      ? [...head, `Repository: ${firstTrusted?.repository ?? 'the repository that deploys the sandbox'}`, `Workflow file: ${workflowFile}`, `Branch: ${branch}`, '',
        '1. Save this workflow as the workflow file above and run it from the Actions tab.',
        '2. Add the repository secret GACPRO_TOKEN (an owner or admin creates it in GAC Pro: Settings → API tokens → Run verification checks from CI).',
        '3. An owner or admin trusts the workflow once in GAC Pro: Settings → Security → CI identities.', '', workflow].join('\n')
      : [...head, '',
        '1. Register the runner once with the one-time code an owner or admin creates in GAC Pro (Setup → In-network collectors → Add a verification runner).',
        '2. Download the check from the target plan in GAC Pro into the folder you run it from.',
        '3. Run it:', '', verifyCmd].join('\n');
  };
  const mailto = (t: Tab) => {
    const body = instructions(t);
    return `mailto:?subject=${encodeURIComponent(`Please run the GAC Pro feasibility check: ${title}`)}&body=${encodeURIComponent(body.length > 4000 ? `${body.slice(0, 4000)}\n…` : body)}`;
  };

  const say = (text: string) => { setAnnounce(''); window.setTimeout(() => setAnnounce(text), 60); };
  const createToken = async () => {
    setBusy('token'); setError('');
    try {
      const r = await createApiToken(slug, { label: `GAC Pro verification · ${label}`.slice(0, 200), purpose: 'verification' });
      onSecret('token', r.plaintext);
      setJustCreated('token');
      say('CI token created. It is shown only now: copy it and save it as GACPRO_TOKEN.');
    } catch (e) { setError(e instanceof Error && e.message ? e.message : 'The token could not be created.'); } finally { setBusy(null); }
  };
  const createCode = async () => {
    setBusy('code'); setError('');
    try {
      const stamp = new Date().toISOString().slice(0, 16).replace('T', ' ');
      const r = await createCustomerCollector(slug, `Verification runner ${stamp}`, 'verification_runner');
      onSecret('code', r.registration_code);
      setJustCreated('code');
      say('Registration code created. It works once and is shown only now.');
    } catch (e) { setError(e instanceof Error && e.message ? e.message : 'The registration code could not be created.'); } finally { setBusy(null); }
  };

  const onTabKey = (e: KeyboardEvent<HTMLButtonElement>) => {
    const i = TABS.findIndex((t) => t.key === tab);
    const next = e.key === 'ArrowRight' ? (i + 1) % TABS.length : e.key === 'ArrowLeft' ? (i + TABS.length - 1) % TABS.length : e.key === 'Home' ? 0 : e.key === 'End' ? TABS.length - 1 : -1;
    if (next < 0) return;
    e.preventDefault();
    setTab(TABS[next]!.key);
    tabs.current[TABS[next]!.key]?.focus();
  };

  const trustLine = canManage ? (
    trust.isLoading ? <p className="gp-dim">Checking your CI identities…</p>
      : trust.isError ? <p><span>Couldn't check your CI identities · </span><button type="button" className="gp-cb-textbtn" onClick={() => void trust.refetch()}>Retry</button></p>
      : firstTrusted ? <p><b>Trusted:</b>{` ${firstTrusted.repository} · ${firstTrusted.workflow_path.replace(/^\.github\/workflows\//, '')} · ${branch}`}{trusted.length > 1 ? <span className="gp-dim">{` · +${trusted.length - 1} more`}</span> : null}</p>
      : <p><b>CI identity: not set up yet</b>{' · '}<Link to={trustWorkflowHref(slug, { repository: '', workflow_path: WORKFLOW_FILE, ref: 'main' }, returnTo)}>Trust a workflow</Link></p>
  ) : <p><span>GAC Pro counts the run once an owner or admin trusts your workflow.</span>{' '}<AskAdmin slug={slug} what="trust our CI workflow for verification checks (Settings → Security → CI identities)" subject={`${label}: trust our CI workflow`} compact /></p>;

  return (
    <div className="gp-pv-handoff" role="region" aria-labelledby={headingId}>
      <h5 id={headingId} className="gp-pv-handoff-h" tabIndex={-1}>{`Hand this to whoever runs CI · ${title}`}</h5>
      <div role="tablist" aria-label={`How to run it · ${title}`} className="gp-pv-tabs">
        {TABS.map((t) => (
          <button key={t.key} ref={(el) => { tabs.current[t.key] = el; }} type="button" role="tab" id={`${uid}-tab-${t.key}`} aria-controls={`${uid}-panel-${t.key}`}
            aria-selected={tab === t.key} tabIndex={tab === t.key ? 0 : -1} className="gp-pv-tab" onClick={() => setTab(t.key)} onKeyDown={onTabKey}>{t.label}</button>
        ))}
      </div>
      <p className="gp-dim">In-network collectors run standing checks only, not feasibility checks.</p>
      <div role="tabpanel" id={`${uid}-panel-${tab}`} aria-labelledby={`${uid}-tab-${tab}`} className="gp-pv-tabpanel">
        {tab === 'github' ? (
          <>
            <div className="gp-pv-first" role="group" aria-label="Do this first"><span className="gp-pv-first-h">Do this first</span>{trustLine}</div>
            <p className="gp-dim">Save this in the repo that deploys the sandbox; run it from Actions.</p>
            {!ready ? <NotPublished gap={gap} /> : (
              <>
                {signer!.nonProduction ? <p className="gp-cb-tag">Non-production signer (staging only)</p> : null}
                <CommandBlock label={`GitHub workflow · ${title}`} text={workflow} copyLabel="Copy workflow" />
              </>
            )}
            <ol className="gp-pv-steps">
              <li>
                <span>Add the repository secret <code>GACPRO_TOKEN</code>: a token that can only run verification checks.</span>{' '}
                {secrets.token ? <ShownOnce label="CI token" value={secrets.token} note="Save it as GACPRO_TOKEN" focusNow={justCreated === 'token'} onFocused={clearJust} />
                  : canManage ? <Button compact variant="secondary" aria-busy={busy === 'token' || undefined} onClick={() => void createToken()}>{busy === 'token' ? 'Creating…' : 'Create the CI token'}</Button>
                  : <AskAdmin slug={slug} what="create a CI token for verification checks" subject={`${label}: CI token for the feasibility checks`} compact />}
              </li>
              <li>
                <span>The runner release is pinned in the workflow:</span>
                {ready ? <>
                  <CommandBlock label="Runner release URL" text={release!.archiveUrl} copyLabel="Copy URL" />
                  <CommandBlock label="Runner release sha256" text={release!.archiveSha256} copyLabel="Copy sha256" />
                </> : <NotPublished gap={gap} />}
              </li>
            </ol>
          </>
        ) : (
          <ol className="gp-pv-steps">
            <li>
              <span>Register the runner once (Docker, on the machine that reaches your sandbox). Already registered? Skip to step 2.</span>{' '}
              {!ready || !release!.imageRef ? <NotPublished gap={gap} />
                : secrets.code ? <CodeShownOnce command={runnerRegisterCommand(secrets.code, origin, release!.imageRef)} focusNow={justCreated === 'code'} onFocused={clearJust} />
                : canManage ? <Button compact variant="secondary" aria-busy={busy === 'code' || undefined} onClick={() => void createCode()}>{busy === 'code' ? 'Creating…' : 'Create a registration code'}</Button>
                : <AskAdmin slug={slug} what="create a verification runner registration code" subject={`${label}: verification runner for the feasibility checks`} compact />}
            </li>
            <li>
              <span>Download the check into the folder you run it from.</span>{' '}
              <a className="gp-linkbtn gp-linkbtn--compact" data-variant="ghost" href={download} onClick={ready ? onHandedOff : undefined} aria-label={`Download the check · ${title}`}>Download the check</a>
            </li>
            <li>Run it:
              {ready && release!.imageRef ? <CommandBlock label={`Runner command · ${title}`} text={verifyCmd} copyLabel="Copy command" /> : <NotPublished gap={gap} />}
            </li>
          </ol>
        )}
        {error ? <Alert variant="error"><p>{error}</p></Alert> : null}
      </div>
      <p role="status" className="sr-only">{announce}</p>
      {ready && (tab === 'github' || release!.imageRef) ? (
        <span className="gp-actions">
          <Button compact variant="ghost" onClick={() => { void copy(instructions(tab), 'Instructions'); onHandedOff(); }}>Copy instructions<span className="sr-only">{` · ${title}`}</span></Button>
          <a className="gp-linkbtn gp-linkbtn--compact" data-variant="ghost" href={mailto(tab)} onClick={onHandedOff}>Email to a teammate<span className="sr-only">{` · ${title}`}</span></a>
          <span role="status" className="gp-dim">{msg}</span>
        </span>
      ) : null}
    </div>
  );
}
